/**
 * Country Configuration
 *
 * The heart of the multi-country system. Each entry defines everything that
 * varies per country/locale: currency, features, shipping, and UX settings.
 *
 * To add a new country:
 * 1. Add its locale to SUPPORTED_LOCALES in intl.config.ts
 * 2. Add its configuration entry here in COUNTRY_CONFIG
 * 3. Create translation files in public/locales/{locale}/
 */

import type { CountryConfig } from '@template-project/storefront-shared';
import type { SupportedLocale } from './intl.config';

/**
 * Per-country configuration map.
 * Keyed by locale string for direct O(1) lookup.
 */
export const COUNTRY_CONFIG: Record<SupportedLocale, CountryConfig> = {
  'en-US': {
    locale: 'en-US',
    localePrefix: '',
    countryCode: 'US',
    countryName: 'United States',
    language: 'en',
    currency: {
      code: 'USD',
      symbol: '$',
      format: '$',
      decimals: 2,
    },
    features: {
      freeShippingThreshold: 5000,
      measureUnit: 'inches',
      vatNotice: null,
      supported: true,
    },
  },

  'es-US': {
    locale: 'es-US',
    localePrefix: '/es-US',
    countryCode: 'US',
    countryName: 'Estados Unidos',
    language: 'es',
    currency: {
      code: 'USD',
      symbol: '$',
      format: '$',
      decimals: 2,
    },
    features: {
      freeShippingThreshold: 5000,
      measureUnit: 'inches',
      vatNotice: null,
      supported: true,
    },
  },

  'en-CA': {
    locale: 'en-CA',
    localePrefix: '/en-CA',
    countryCode: 'CA',
    countryName: 'Canada',
    language: 'en',
    currency: {
      code: 'CAD',
      symbol: '$',
      format: 'CA$',
      decimals: 2,
    },
    features: {
      freeShippingThreshold: 6500,
      measureUnit: 'inches',
      vatNotice: null,
      supported: true,
    },
  },

  'fr-CA': {
    locale: 'fr-CA',
    localePrefix: '/fr-CA',
    countryCode: 'CA',
    countryName: 'Canada',
    language: 'fr',
    currency: {
      code: 'CAD',
      symbol: '$',
      format: 'CA$',
      decimals: 2,
    },
    features: {
      freeShippingThreshold: 6500,
      measureUnit: 'inches',
      vatNotice: null,
      supported: true,
    },
  },

  'en-GB': {
    locale: 'en-GB',
    localePrefix: '/en-GB',
    countryCode: 'GB',
    countryName: 'United Kingdom',
    language: 'en',
    currency: {
      code: 'GBP',
      symbol: '£',
      format: '£',
      decimals: 2,
    },
    features: {
      freeShippingThreshold: 2800,
      measureUnit: 'cm',
      vatNotice: 'Price includes VAT. No surprises!',
      supported: true,
    },
  },

  'en-AU': {
    locale: 'en-AU',
    localePrefix: '/en-AU',
    countryCode: 'AU',
    countryName: 'Australia',
    language: 'en',
    currency: {
      code: 'AUD',
      symbol: '$',
      format: 'AU$',
      decimals: 2,
    },
    features: {
      freeShippingThreshold: 7000,
      measureUnit: 'cm',
      vatNotice: null,
      supported: true,
    },
  },

  'en-NZ': {
    locale: 'en-NZ',
    localePrefix: '/en-NZ',
    countryCode: 'NZ',
    countryName: 'New Zealand',
    language: 'en',
    currency: {
      code: 'NZD',
      symbol: '$',
      format: 'NZ$',
      decimals: 2,
    },
    features: {
      freeShippingThreshold: 7000,
      measureUnit: 'cm',
      vatNotice: null,
      supported: true,
    },
  },

  'en-SG': {
    locale: 'en-SG',
    localePrefix: '/en-SG',
    countryCode: 'SG',
    countryName: 'Singapore',
    language: 'en',
    currency: {
      code: 'SGD',
      symbol: '$',
      format: 'S$',
      decimals: 2,
    },
    features: {
      freeShippingThreshold: 7000,
      measureUnit: 'cm',
      vatNotice: null,
      supported: true,
    },
  },

  'en-NL': {
    locale: 'en-NL',
    localePrefix: '/en-NL',
    countryCode: 'NL',
    countryName: 'Netherlands',
    language: 'en',
    currency: {
      code: 'EUR',
      symbol: '€',
      format: '€',
      decimals: 2,
    },
    features: {
      freeShippingThreshold: 5000,
      measureUnit: 'cm',
      vatNotice: 'Price includes VAT. No surprises!',
      supported: true,
    },
  },

  'en-SE': {
    locale: 'en-SE',
    localePrefix: '/en-SE',
    countryCode: 'SE',
    countryName: 'Sweden',
    language: 'en',
    currency: {
      code: 'SEK',
      symbol: 'kr',
      format: 'kr',
      decimals: 2,
    },
    features: {
      freeShippingThreshold: 70000,
      measureUnit: 'cm',
      vatNotice: 'Price includes VAT. No surprises!',
      supported: true,
    },
  },

  'en-DE': {
    locale: 'en-DE',
    localePrefix: '/en-DE',
    countryCode: 'DE',
    countryName: 'Germany',
    language: 'en',
    currency: {
      code: 'EUR',
      symbol: '€',
      format: '€',
      decimals: 2,
    },
    features: {
      freeShippingThreshold: 5000,
      measureUnit: 'cm',
      vatNotice: 'Price includes VAT. No surprises!',
      supported: true,
    },
  },

  'de-DE': {
    locale: 'de-DE',
    localePrefix: '/de-DE',
    countryCode: 'DE',
    countryName: 'Deutschland',
    language: 'de',
    currency: {
      code: 'EUR',
      symbol: '€',
      format: '€',
      decimals: 2,
    },
    features: {
      freeShippingThreshold: 5000,
      measureUnit: 'cm',
      vatNotice: 'Preis inkl. MwSt. Keine Überraschungen!',
      supported: true,
    },
  },

  'en-DK': {
    locale: 'en-DK',
    localePrefix: '/en-DK',
    countryCode: 'DK',
    countryName: 'Denmark',
    language: 'en',
    currency: {
      code: 'DKK',
      symbol: 'kr',
      format: 'kr',
      decimals: 2,
    },
    features: {
      freeShippingThreshold: 50000,
      measureUnit: 'cm',
      vatNotice: 'Price includes VAT. No surprises!',
      supported: true,
    },
  },

  'en-FI': {
    locale: 'en-FI',
    localePrefix: '/en-FI',
    countryCode: 'FI',
    countryName: 'Finland',
    language: 'en',
    currency: {
      code: 'EUR',
      symbol: '€',
      format: '€',
      decimals: 2,
    },
    features: {
      freeShippingThreshold: 5000,
      measureUnit: 'cm',
      vatNotice: 'Price includes VAT. No surprises!',
      supported: true,
    },
  },
};

/**
 * Get all supported country configs as an array.
 */
export function getAllCountryConfigs(): CountryConfig[] {
  return Object.values(COUNTRY_CONFIG);
}

/**
 * Get all supported country configs that are currently active.
 */
export function getActiveCountryConfigs(): CountryConfig[] {
  return getAllCountryConfigs().filter((config) => config.features.supported);
}

/**
 * A country group represents a single country that may have
 * multiple language variants (e.g. Germany: English + Deutsch).
 */
export interface CountryGroup {
  /** ISO 3166-1 alpha-2 country code */
  countryCode: string;
  /** Canonical display name (always the English name for consistency) */
  displayName: string;
  /** Currency shared by all locales in this country */
  currency: CountryConfig['currency'];
  /** Available locale configs, ordered with the English variant first */
  locales: CountryConfig[];
}

/** Human-readable language labels keyed by ISO 639-1 code */
const LANGUAGE_LABELS: Record<string, string> = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  pt: 'Português',
};

/**
 * Return a human-readable label for a language code.
 */
export function getLanguageLabel(langCode: string): string {
  return LANGUAGE_LABELS[langCode] ?? langCode.toUpperCase();
}

/**
 * Group active country configs by country code.
 *
 * Countries with multiple locales (e.g. US: en-US + es-US) are collapsed
 * into a single entry with an array of locale options. The `displayName`
 * is always the English name so the list doesn't show duplicates like
 * "Germany" and "Deutschland".
 *
 * Groups are returned in the same order as the first locale for each
 * country appears in SUPPORTED_LOCALES.
 */
export function getCountryGroups(): CountryGroup[] {
  const configs = getActiveCountryConfigs();
  const groupMap = new Map<string, CountryGroup>();

  for (const config of configs) {
    const existing = groupMap.get(config.countryCode);
    if (existing) {
      existing.locales.push(config);
    } else {
      groupMap.set(config.countryCode, {
        countryCode: config.countryCode,
        displayName: config.countryName,
        currency: config.currency,
        locales: [config],
      });
    }
  }

  // Ensure the display name is always the English variant
  for (const group of groupMap.values()) {
    const englishLocale = group.locales.find((l) => l.language === 'en');
    if (englishLocale) {
      group.displayName = englishLocale.countryName;
    }
  }

  return Array.from(groupMap.values());
}
